import {
  getToken,
  isSubscriptionRequired,
  pointOfSaleApi,
  productApi,
} from "@repo/api-client";
import { useEffect, useRef, type ReactNode } from "react";
import {
  isRouteErrorResponse,
  Outlet,
  redirect,
  useRevalidator,
  useRouteError,
} from "react-router";
import { CreditCard } from "lucide-react";
import { Button } from "@repo/ui/components/button";
import { EmptyState } from "@repo/ui/components/empty-state";
import { Header } from "../components/header";
import { useNetworkStatus } from "@repo/ui/hooks/use-network-status";
import { processPendingSales } from "../utils/pending-sales";

function Layout() {
  // const matches = useMatches();
  const isOnline = useNetworkStatus();
  const wasOnlineRef = useRef<boolean | null>(null);
  const isProcessingRef = useRef(false);
  const revalidator = useRevalidator();

  // const backLink = (
  //   matches.find((match: any) => {
  //     return match.handle?.backLink;
  //   })?.handle as any
  // )?.backLink;

  // Procesar ventas pendientes cuando se recupera la conexión o al cargar la app
  useEffect(() => {
    // Si es la primera vez (wasOnlineRef.current es null) y hay conexión, procesar
    // O si se recupera la conexión (de offline a online)
    const shouldProcess =
      isOnline &&
      !isProcessingRef.current &&
      (wasOnlineRef.current === null || wasOnlineRef.current === false);

    if (shouldProcess) {
      isProcessingRef.current = true;
      processPendingSales(productApi.createSaleItem)
        .then(() => {
          console.log("Pending sales processed successfully");
        })
        .catch((error) => {
          if (isSubscriptionRequired(error)) {
            // The startup loader answers 402 too: the blocked notice replaces the POS.
            revalidator.revalidate();
            return;
          }
          console.error("Error processing pending sales:", error);
        })
        .finally(() => {
          isProcessingRef.current = false;
        });
    }

    // Actualizar el estado previo de conexión
    if (wasOnlineRef.current === null || wasOnlineRef.current !== isOnline) {
      wasOnlineRef.current = isOnline;
    }
  }, [isOnline]);

  return (
    <Frame header={<Header />}>
      <Outlet />
    </Frame>
  );
}

function Frame({
  header,
  children,
}: {
  header?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex h-screen flex-col">
      {/* <Header backLink={backLink} /> */}
      {header}
      <div className="flex-1 flex flex-col items-center justify-center gap-2 min-h-0 max-w-6xl mx-auto w-full px-4 relative">
        {children}
      </div>
      <div className="text-center text-base text-primary w-full mb-1">
        playpadel.io
      </div>
    </div>
  );
}

/**
 * The API answers 402 to every POS request while the club has no subscription
 * in force, the startup data included. Any other error goes up, as before.
 */
function SubscriptionExpired() {
  const error = useRouteError();
  const revalidator = useRevalidator();

  if (!isRouteErrorResponse(error) || error.status !== 402) throw error;
  // No header: its club logo comes from the startup data, which is what failed.
  return (
    <Frame>
      <EmptyState
        icon={CreditCard}
        title="La suscripción del club venció"
        description="El punto de venta queda bloqueado hasta que el dueño del club la renueve. Las ventas pendientes se enviarán cuando vuelva a funcionar."
        actions={
          <Button
            variant="pos"
            size="2xl"
            loading={revalidator.state === "loading"}
            onClick={() => revalidator.revalidate()}
          >
            Reintentar
          </Button>
        }
      />
    </Frame>
  );
}

export const posLayoutId = "pos-layout";

export const route = {
  id: posLayoutId,
  element: <Layout />,
  errorElement: <SubscriptionExpired />,
  loader: async () => {
    const token = getToken();
    if (!token) {
      return redirect("/login");
    }
    return await pointOfSaleApi.getStartupData();
  },
};
